/**
 * Rapid X - Professional Incident Reporting Module
 * 
 * Main incident reporting and history workspace that:
 * - Uses Command Centre feed styling as baseline
 * - Provides structured incident table/list
 * - Includes comprehensive search/filtering
 * - Supports View/PDF/Email/Add Images actions
 * - Shows proper incident detail reports
 * 
 * Architecture:
 * - Command Centre = live feed / monitoring
 * - Incidents tab = searchable report archive + action centre
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatDate, formatTime, severityColors, statusColors } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Shield, Plus, Search, Filter, MapPin, Clock, User, AlertTriangle, FileText,
  Upload, X, Eye, Edit3, Send, Loader2, Image, Download, RefreshCw, Camera,
  Trash2, Archive, MoreVertical, Building2, Calendar, Hash, CheckCircle2,
  XCircle, Mail, Printer, ExternalLink, ChevronRight, AlertCircle
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  high: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  medium: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  low: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
};

const STATUS_COLORS = {
  draft: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/30' },
  submitted: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
  under_review: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  approved: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  released: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  closed: { bg: 'bg-zinc-600/20', text: 'text-zinc-500', border: 'border-zinc-600/30' },
  open: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  resolved: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

const INCIDENT_TYPES = [
  'Trespass', 'Theft', 'Vandalism', 'Assault', 'Fire', 'Medical Emergency',
  'Suspicious Activity', 'Vehicle Incident', 'Property Damage', 'Security Breach',
  'Alarm Activation', 'Customer Complaint', 'Staff Issue', 'Other'
];

export default function IncidentsPage() {
  const { api, user, isAdmin, isOfficer } = useAuth();
  
  // List state
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ open: 0, critical: 0, today: 0 });
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedOfficer, setSelectedOfficer] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  
  // Reference data
  const [sites, setSites] = useState([]);
  const [officers, setOfficers] = useState([]);
  
  // Detail dialog state
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // New incident dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newIncidentData, setNewIncidentData] = useState({
    title: '', description: '', site_id: '', incident_type: '', severity: 'medium'
  });
  const [creating, setCreating] = useState(false);
  const [activeShiftSiteId, setActiveShiftSiteId] = useState(null);
  const [activeShiftSiteName, setActiveShiftSiteName] = useState(null);
  
  // Fetch active shift to auto-assign site for officers
  useEffect(() => {
    const fetchActiveShift = async () => {
      try {
        const res = await api.get('/shifts/my-shifts');
        const shifts = res.data || [];
        // Find the first active/started shift
        const activeShift = shifts.find(s =>
          ['started', 'patrol_active', 'signed_on', 'clocked_in', 'accepted', 'fit_for_work', 'sop_acknowledged', 'geofence_validated'].includes(s.workflow_stage) &&
          !['completed', 'cancelled', 'auto_closed', 'force_closed', 'expired', 'closed'].includes(s.status)
        );
        if (activeShift?.site_id) {
          setActiveShiftSiteId(activeShift.site_id);
          setActiveShiftSiteName(activeShift.site_name || null);
        }
      } catch (err) {
        // Non-critical - admin users may not have shifts
        console.log('No active shift found for auto-assignment');
      }
    };
    fetchActiveShift();
  }, []);

  // Auto-populate site when opening new incident dialog
  useEffect(() => {
    if (showNewDialog && activeShiftSiteId && !newIncidentData.site_id) {
      setNewIncidentData(prev => ({ ...prev, site_id: activeShiftSiteId }));
    }
  }, [showNewDialog, activeShiftSiteId]);

  // Photo upload state
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Send to client state
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Status change state
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Bulk selection state (Admin only)
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkArchiveMode, setBulkArchiveMode] = useState(true);
  
  // Set default date range on mount
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateTo(today.toISOString().split('T')[0]);
    setDateFrom(lastMonth.toISOString().split('T')[0]);
    
    loadReferenceData();
  }, []);
  
  // Load incidents when filters change
  useEffect(() => {
    if (dateFrom && dateTo) {
      loadIncidents();
    }
  }, [dateFrom, dateTo, selectedSite, selectedOfficer, selectedType, selectedStatus, selectedSeverity]);
  
  const loadReferenceData = async () => {
    // Load sites and users separately to handle permission differences
    // Officers may not have access to /users endpoint
    try {
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
    
    try {
      const usersRes = await api.get('/users');
      setOfficers((usersRes.data || []).filter(u => 
        ['security_officer', 'supervisor', 'super_admin'].includes(u.role)
      ));
    } catch (error) {
      // Officers may not have access to users list - this is expected
      console.log('Users list not available (may require admin role)');
    }
  };
  
  const loadIncidents = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (dateFrom) params.append('from_date', dateFrom);
      if (dateTo) params.append('to_date', dateTo);
      if (selectedSite !== 'all') params.append('site_id', selectedSite);
      if (selectedOfficer !== 'all') params.append('reported_by', selectedOfficer);
      if (selectedType !== 'all') params.append('incident_type', selectedType);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      if (selectedSeverity !== 'all') params.append('severity', selectedSeverity);
      params.append('limit', '100');
      
      const response = await api.get(`/incidents?${params.toString()}`);
      let data = response.data || [];
      
      // Apply search filter client-side
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        data = data.filter(inc => 
          inc.title?.toLowerCase().includes(query) ||
          inc.description?.toLowerCase().includes(query) ||
          inc.ir_number?.toLowerCase().includes(query) ||
          inc.site_name?.toLowerCase().includes(query)
        );
      }
      
      setIncidents(data);
      setTotalCount(data.length);
      
      // Calculate stats
      const openCount = data.filter(i => ['open', 'draft', 'submitted', 'under_review'].includes(i.status?.toLowerCase())).length;
      const criticalCount = data.filter(i => i.severity === 'critical').length;
      const todayStr = new Date().toISOString().split('T')[0];
      const todayCount = data.filter(i => i.created_at?.startsWith(todayStr)).length;
      
      setStats({ open: openCount, critical: criticalCount, today: todayCount });
    } catch (error) {
      console.error('Error loading incidents:', error);
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };
  
  const loadIncidentDetail = async (incidentId) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/incidents/${incidentId}`);
      setSelectedIncident(response.data);
    } catch (error) {
      console.error('Error loading incident detail:', error);
      toast.error('Failed to load incident details');
    } finally {
      setDetailLoading(false);
    }
  };
  
  const handleViewIncident = async (incident) => {
    setSelectedIncident(incident);
    setShowDetailDialog(true);
    await loadIncidentDetail(incident.id);
  };
  
  const handleCreateIncident = async () => {
    if (!newIncidentData.title || !newIncidentData.site_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      setCreating(true);
      const response = await api.post('/incidents', newIncidentData);
      toast.success('Incident created successfully');
      setShowNewDialog(false);
      setNewIncidentData({ title: '', description: '', site_id: activeShiftSiteId || '', incident_type: '', severity: 'medium' });
      loadIncidents();
      
      // Open the new incident for editing
      handleViewIncident(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create incident');
    } finally {
      setCreating(false);
    }
  };
  
  const handleExportPDF = async (incident) => {
    try {
      const response = await api.get(`/reports/incident/${incident.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident_${incident.ir_number || incident.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (error) {
      if (error.response?.status === 501) {
        toast.error('Incident PDF generation coming soon');
      } else {
        toast.error('Failed to generate PDF');
      }
    }
  };
  
  const handleSendToClient = async () => {
    if (!sendEmail || !selectedIncident) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post(`/reports/incident/${selectedIncident.id}/send`, {
        recipient_email: sendEmail,
        message: sendMessage
      });
      
      if (response.data.success) {
        toast.success(`Report sent to ${sendEmail}`);
        // Refresh incident list to update sent status
        loadIncidents();
      } else {
        toast.warning(response.data.message || 'Report generated but email not sent');
      }
      
      setShowSendDialog(false);
      setSendEmail('');
      setSendMessage('');
    } catch (error) {
      toast.error('Failed to send report');
    } finally {
      setSending(false);
    }
  };
  
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !selectedIncident) return;
    
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        await api.post(`/incidents/${selectedIncident.id}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${files.length} photo(s) uploaded`);
      await loadIncidentDetail(selectedIncident.id);
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Handle status change (Submit, Review, Close)
  const handleStatusChange = async (incident, newStatus) => {
    try {
      setChangingStatus(true);
      await api.post(`/incidents/${incident.id}/status?new_status=${newStatus}`);
      toast.success(`Incident status changed to ${newStatus.replace(/_/g, ' ')}`);
      
      // Refresh the incident detail if viewing
      if (selectedIncident?.id === incident.id) {
        await loadIncidentDetail(incident.id);
      }
      loadIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };
  
  // Handle delete incident
  const handleDeleteIncident = async () => {
    if (!deleteTarget) return;
    
    try {
      setDeleting(true);
      await api.delete(`/incidents/${deleteTarget.id}`);
      toast.success('Incident deleted');
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      
      // Close detail dialog if deleting the currently viewed incident
      if (selectedIncident?.id === deleteTarget.id) {
        setShowDetailDialog(false);
        setSelectedIncident(null);
      }
      
      loadIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete incident');
    } finally {
      setDeleting(false);
    }
  };
  
  // Confirm delete
  const confirmDelete = (incident) => {
    setDeleteTarget(incident);
    setShowDeleteDialog(true);
  };
  
  // Bulk selection handlers (Admin only)
  const toggleSelectIncident = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.length === incidents.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(incidents.map(i => i.id));
    }
  };
  
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setBulkDeleting(true);
    try {
      await api.post('/feeds/incidents/bulk-delete', {
        item_ids: selectedIds,
        archive: bulkArchiveMode
      });
      toast.success(`${selectedIds.length} incidents ${bulkArchiveMode ? 'archived' : 'deleted'}`);
      setSelectedIds([]);
      setShowBulkDeleteDialog(false);
      loadIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete incidents');
    } finally {
      setBulkDeleting(false);
    }
  };
  
  const getSeverityBadge = (severity) => {
    const config = SEVERITY_COLORS[severity] || SEVERITY_COLORS.low;
    return (
      <Badge className={`${config.bg} ${config.text} border ${config.border} text-xs`}>
        {severity?.toUpperCase()}
      </Badge>
    );
  };
  
  const getStatusBadge = (status) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');
    const config = STATUS_COLORS[normalizedStatus] || STATUS_COLORS.open;
    return (
      <Badge className={`${config.bg} ${config.text} border ${config.border} text-xs`}>
        {status?.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };
  
  const getReportStatusBadge = (incident) => {
    // Only show additional badges for sent/final/archived - not duplicate Draft
    if (incident.sent_to_client) {
      return <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs ml-2">Sent</Badge>;
    }
    if (incident.is_archived) {
      return <Badge className="bg-zinc-500/20 text-zinc-400 border border-zinc-500/30 text-xs ml-2">Archived</Badge>;
    }
    // Don't show duplicate status badges - the getStatusBadge already shows the workflow status
    return null;
  };

  return (
    <div className="space-y-6" data-testid="incidents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            INCIDENT REPORTS
          </h1>
          <p className="text-sm text-zinc-500">Incident reporting and history workspace</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadIncidents}
            disabled={loading}
            className="border-zinc-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowNewDialog(true)}
            className="bg-red-500 hover:bg-red-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Incident
          </Button>
        </div>
      </div>
      
      {/* Admin Bulk Actions Bar */}
      {isAdmin && selectedIds.length > 0 && (
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  {selectedIds.length} selected
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedIds([])}
                  className="text-zinc-400 hover:text-white"
                >
                  Clear Selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setBulkArchiveMode(true);
                    setShowBulkDeleteDialog(true);
                  }}
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  data-testid="bulk-archive-btn"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setBulkArchiveMode(false);
                    setShowBulkDeleteDialog(true);
                  }}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{totalCount}</p>
                <p className="text-xs text-zinc-500">Total Incidents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.open}</p>
                <p className="text-xs text-zinc-500">Open Incidents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.critical}</p>
                <p className="text-xs text-zinc-500">Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.today}</p>
                <p className="text-xs text-zinc-500">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {/* Search */}
            <div className="col-span-2 space-y-1">
              <Label className="text-zinc-500 text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="IR#, title, description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadIncidents()}
                  className="pl-9 bg-[#09090b] border-[#3f3f46] text-white h-9"
                />
              </div>
            </div>
            
            {/* Date From */}
            <div className="space-y-1">
              <Label className="text-zinc-500 text-xs">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] text-white h-9"
              />
            </div>
            
            {/* Date To */}
            <div className="space-y-1">
              <Label className="text-zinc-500 text-xs">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] text-white h-9"
              />
            </div>
            
            {/* Site */}
            <div className="space-y-1">
              <Label className="text-zinc-500 text-xs">Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white h-9">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.filter(s => s.name).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Type */}
            <div className="space-y-1">
              <Label className="text-zinc-500 text-xs">Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white h-9">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="all">All Types</SelectItem>
                  {INCIDENT_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Severity */}
            <div className="space-y-1">
              <Label className="text-zinc-500 text-xs">Severity</Label>
              <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white h-9">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status */}
            <div className="space-y-1">
              <Label className="text-zinc-500 text-xs">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white h-9">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Incident List */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white font-rajdhani flex items-center gap-2">
            <FileText className="w-5 h-5 text-zinc-400" />
            Incident Records
            <Badge className="ml-2 bg-zinc-700 text-zinc-300">{totalCount}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No incidents found</p>
              <p className="text-zinc-600 text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#3f3f46] text-xs text-zinc-500 uppercase">
                    {isAdmin && (
                      <th className="p-3 w-10">
                        <Checkbox
                          checked={selectedIds.length === incidents.length && incidents.length > 0}
                          onCheckedChange={toggleSelectAll}
                          className="border-zinc-600"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                    )}
                    <th className="text-left p-3 font-medium">IR #</th>
                    <th className="text-left p-3 font-medium">Site</th>
                    <th className="text-left p-3 font-medium">Occurred</th>
                    <th className="text-left p-3 font-medium">Entered</th>
                    <th className="text-left p-3 font-medium">Officer</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-left p-3 font-medium">Severity</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-center p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((incident, idx) => (
                    <tr 
                      key={incident.id}
                      className={`border-b border-[#3f3f46]/50 hover:bg-[#09090b] transition-colors ${
                        incident.severity === 'critical' ? 'bg-red-500/5' : ''
                      } ${selectedIds.includes(incident.id) ? 'bg-blue-500/10' : ''}`}
                    >
                      {isAdmin && (
                        <td className="p-3">
                          <Checkbox
                            checked={selectedIds.includes(incident.id)}
                            onCheckedChange={() => toggleSelectIncident(incident.id)}
                            className="border-zinc-600"
                            data-testid={`select-incident-${incident.id}`}
                          />
                        </td>
                      )}
                      <td className="p-3">
                        <span className="font-mono text-sm text-blue-400">{incident.ir_number || '-'}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-zinc-500" />
                          <span className="text-white text-sm truncate max-w-[150px]">{incident.site_name || '-'}</span>
                        </div>
                      </td>
                      <td className="p-3 text-zinc-400 text-sm">
                        <div>{formatDate(incident.occurred_at || incident.created_at)}</div>
                        <div className="text-xs text-zinc-500">{formatTime(incident.occurred_at || incident.created_at)}</div>
                      </td>
                      <td className="p-3 text-zinc-400 text-sm">
                        <div>{formatDate(incident.created_at)}</div>
                        <div className="text-xs text-zinc-500">{formatTime(incident.created_at)}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-500" />
                          <span className="text-zinc-300 text-sm truncate max-w-[120px]">
                            {incident.reported_by_name || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-zinc-300 text-sm">{incident.incident_type || '-'}</td>
                      <td className="p-3">{getSeverityBadge(incident.severity)}</td>
                      <td className="p-3">
                        <div className="flex items-center">
                          {getStatusBadge(incident.status)}
                          {getReportStatusBadge(incident)}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          {/* View Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewIncident(incident)}
                            className="h-8 w-8 p-0 hover:bg-blue-500/20"
                            title="View"
                          >
                            <Eye className="w-4 h-4 text-blue-400" />
                          </Button>
                          
                          {/* PDF Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExportPDF(incident)}
                            className="h-8 w-8 p-0 hover:bg-zinc-700"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4 text-zinc-400" />
                          </Button>
                          
                          {/* Delete Button - visible for Draft or Admin */}
                          {(incident.status?.toLowerCase() === 'draft' || isAdmin) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => confirmDelete(incident)}
                              className="h-8 w-8 p-0 hover:bg-red-500/20"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          )}
                          
                          {/* More Actions Menu */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-zinc-700">
                                <MoreVertical className="w-4 h-4 text-zinc-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#18181b] border-[#3f3f46]">
                              {/* Submit Button for Draft incidents */}
                              {incident.status?.toLowerCase() === 'draft' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleStatusChange(incident, 'SUBMITTED')}
                                    disabled={changingStatus}
                                    className="text-emerald-400 focus:bg-emerald-500/20 focus:text-emerald-300"
                                  >
                                    <Send className="w-4 h-4 mr-2" />
                                    Submit Incident
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-[#3f3f46]" />
                                </>
                              )}
                              
                              {/* Status change options for supervisors/admins */}
                              {isAdmin && incident.status?.toLowerCase() === 'submitted' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleStatusChange(incident, 'UNDER_REVIEW')}
                                    disabled={changingStatus}
                                    className="text-amber-400 focus:bg-amber-500/20 focus:text-amber-300"
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Mark Under Review
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-[#3f3f46]" />
                                </>
                              )}
                              
                              {isAdmin && incident.status?.toLowerCase() === 'under_review' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleStatusChange(incident, 'CLOSED')}
                                    disabled={changingStatus}
                                    className="text-zinc-400 focus:bg-zinc-500/20 focus:text-zinc-300"
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    Close Incident
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-[#3f3f46]" />
                                </>
                              )}
                              
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedIncident(incident);
                                  setShowPhotoDialog(true);
                                }}
                                className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                              >
                                <Image className="w-4 h-4 mr-2" />
                                Add Images
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedIncident(incident);
                                  setShowSendDialog(true);
                                }}
                                className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Send to Client
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-[#3f3f46]" />
                              <DropdownMenuItem 
                                onClick={() => window.print()}
                                className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                              >
                                <Printer className="w-4 h-4 mr-2" />
                                Print
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Incident Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-red-400" />
              <div>
                <span className="text-white">Incident Report</span>
                {selectedIncident?.ir_number && (
                  <span className="ml-2 font-mono text-blue-400 text-sm">{selectedIncident.ir_number}</span>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : selectedIncident && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="flex items-center gap-3 flex-wrap">
                {getSeverityBadge(selectedIncident.severity)}
                {getStatusBadge(selectedIncident.status)}
                {getReportStatusBadge(selectedIncident)}
              </div>
              
              {/* Title */}
              <div>
                <h2 className="text-xl font-semibold text-white">{selectedIncident.title}</h2>
                {selectedIncident.incident_type && (
                  <p className="text-zinc-400 text-sm mt-1">Type: {selectedIncident.incident_type}</p>
                )}
              </div>
              
              {/* Location & Time Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-zinc-500 text-xs">Site / Location</Label>
                  <p className="text-white">{selectedIncident.site_name || '-'}</p>
                  {selectedIncident.location_detail && (
                    <p className="text-zinc-400 text-sm">{selectedIncident.location_detail}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-500 text-xs">Reported By</Label>
                  <p className="text-white">{selectedIncident.reported_by_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-500 text-xs">Date/Time Occurred</Label>
                  <p className="text-white">{formatDateTime(selectedIncident.occurred_at || selectedIncident.created_at)}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-500 text-xs">Date/Time Entered</Label>
                  <p className="text-white">{formatDateTime(selectedIncident.created_at)}</p>
                </div>
              </div>
              
              {/* Description */}
              {selectedIncident.description && (
                <div className="space-y-2">
                  <Label className="text-zinc-500 text-xs">Incident Narrative</Label>
                  <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46]">
                    <p className="text-zinc-300 text-sm whitespace-pre-wrap">{selectedIncident.description}</p>
                  </div>
                </div>
              )}
              
              {/* Action Taken */}
              {selectedIncident.action_taken && (
                <div className="space-y-2">
                  <Label className="text-zinc-500 text-xs">Action Taken</Label>
                  <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46]">
                    <p className="text-zinc-300 text-sm whitespace-pre-wrap">{selectedIncident.action_taken}</p>
                  </div>
                </div>
              )}
              
              {/* GPS Verification */}
              {(selectedIncident.latitude && selectedIncident.longitude) && (
                <div className="space-y-2">
                  <Label className="text-zinc-500 text-xs">GPS Verification</Label>
                  <div className="p-3 bg-emerald-500/10 rounded border border-emerald-500/30 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400 text-sm">
                      {selectedIncident.latitude.toFixed(6)}, {selectedIncident.longitude.toFixed(6)}
                    </span>
                    {selectedIncident.accuracy_meters && (
                      <span className="text-emerald-400/70 text-xs ml-2">(±{Math.round(selectedIncident.accuracy_meters)}m)</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Related Patrol/Shift */}
              {(selectedIncident.patrol_run_id || selectedIncident.shift_id) && (
                <div className="space-y-2">
                  <Label className="text-zinc-500 text-xs">Related Activity</Label>
                  <div className="flex gap-2">
                    {selectedIncident.patrol_run_id && (
                      <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                        Patrol: {selectedIncident.patrol_run_id}
                      </Badge>
                    )}
                    {selectedIncident.shift_id && (
                      <Badge className="bg-zinc-500/20 text-zinc-400 border border-zinc-500/30">
                        Shift: {selectedIncident.shift_id}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              {/* Photos */}
              {selectedIncident.photos?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-zinc-500 text-xs">Photos ({selectedIncident.photos.length})</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedIncident.photos.map((photo, idx) => (
                      <a
                        key={idx}
                        href={photo.url || `${process.env.REACT_APP_BACKEND_URL}${photo.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-square rounded overflow-hidden border border-[#3f3f46] hover:border-blue-500 transition-colors"
                      >
                        <img
                          src={photo.url || `${process.env.REACT_APP_BACKEND_URL}${photo.file_url}`}
                          alt={`Photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 flex-wrap">
            {/* Submit Button for Draft incidents */}
            {selectedIncident?.status?.toLowerCase() === 'draft' && (
              <Button
                onClick={() => handleStatusChange(selectedIncident, 'SUBMITTED')}
                disabled={changingStatus}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {changingStatus ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Incident
              </Button>
            )}
            
            {/* Status change for supervisors */}
            {isAdmin && selectedIncident?.status?.toLowerCase() === 'submitted' && (
              <Button
                onClick={() => handleStatusChange(selectedIncident, 'UNDER_REVIEW')}
                disabled={changingStatus}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {changingStatus ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Mark Under Review
              </Button>
            )}
            
            {isAdmin && selectedIncident?.status?.toLowerCase() === 'under_review' && (
              <Button
                onClick={() => handleStatusChange(selectedIncident, 'CLOSED')}
                disabled={changingStatus}
                className="bg-zinc-600 hover:bg-zinc-700"
              >
                {changingStatus ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Close Incident
              </Button>
            )}
            
            <Button
              variant="outline"
              onClick={() => {
                setShowPhotoDialog(true);
              }}
              className="border-zinc-600"
            >
              <Image className="w-4 h-4 mr-2" />
              Add Images
            </Button>
            <Button
              variant="outline"
              onClick={() => selectedIncident && handleExportPDF(selectedIncident)}
              className="border-zinc-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSendDialog(true)}
              className="border-zinc-600"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* New Incident Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-red-400" />
              New Incident Report
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Create a new incident report
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Title *</Label>
              <Input
                placeholder="Brief incident title"
                value={newIncidentData.title}
                onChange={(e) => setNewIncidentData({...newIncidentData, title: e.target.value})}
                className="bg-[#09090b] border-[#3f3f46] text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Site *</Label>
                {activeShiftSiteId && newIncidentData.site_id === activeShiftSiteId && (
                  <p className="text-xs text-emerald-400 mb-1" data-testid="auto-assigned-site-hint">
                    Auto-assigned from active shift{activeShiftSiteName ? `: ${activeShiftSiteName}` : ''}
                  </p>
                )}
                <Select 
                  value={newIncidentData.site_id} 
                  onValueChange={(v) => setNewIncidentData({...newIncidentData, site_id: v})}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white" data-testid="incident-site-selector">
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {sites.filter(s => s.name).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                    {sites.filter(s => s.name).length === 0 && (
                      <div className="p-2 text-xs text-zinc-500 text-center">No sites available</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Severity</Label>
                <Select 
                  value={newIncidentData.severity} 
                  onValueChange={(v) => setNewIncidentData({...newIncidentData, severity: v})}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Incident Type</Label>
              <Select 
                value={newIncidentData.incident_type} 
                onValueChange={(v) => setNewIncidentData({...newIncidentData, incident_type: v})}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46] text-white">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  {INCIDENT_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                placeholder="Describe the incident..."
                value={newIncidentData.description}
                onChange={(e) => setNewIncidentData({...newIncidentData, description: e.target.value})}
                className="bg-[#09090b] border-[#3f3f46] text-white min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateIncident} 
              className="bg-red-500 hover:bg-red-600"
              disabled={creating}
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Photo Upload Dialog - P0.5 Mobile-First */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-purple-400" />
              Add Photo Evidence
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add photos to incident: {selectedIncident?.title || 'this incident'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Camera Capture - Primary for Mobile */}
            <div>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                id="incident-photo-camera-input"
                data-testid="incident-photo-camera-input"
              />
              <label
                htmlFor="incident-photo-camera-input"
                className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-purple-500/50 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-500/5 transition-all"
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                ) : (
                  <>
                    <Camera className="w-6 h-6 text-purple-400" />
                    <span className="text-purple-300 font-medium">Take Photo</span>
                  </>
                )}
              </label>
            </div>
            
            {/* Gallery/File Upload */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="incident-photo-gallery-input"
              />
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-[#3f3f46] rounded-lg p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                    <p className="text-zinc-400 text-sm">Choose from gallery</p>
                    <p className="text-zinc-600 text-xs mt-1">or drag and drop</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Existing Photos Count */}
            {selectedIncident?.photos?.length > 0 && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
                <p className="text-emerald-400 text-sm flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  {selectedIncident.photos.length} photo(s) attached
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhotoDialog(false)} className="border-zinc-600">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Send to Client Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-400" />
              Send to Client
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Email incident report to client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Recipient Email *</Label>
              <Input
                type="email"
                placeholder="client@example.com"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Message (optional)</Label>
              <Textarea
                placeholder="Add a message..."
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                className="bg-[#09090b] border-[#3f3f46] text-white min-h-[80px]"
              />
            </div>
            
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
              <p className="text-blue-400 text-sm">
                <strong>Report:</strong> {selectedIncident?.title}
              </p>
              <p className="text-blue-300/70 text-xs mt-1">
                Site: {selectedIncident?.site_name}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button 
              onClick={handleSendToClient}
              className="bg-blue-500 hover:bg-blue-600"
              disabled={sending || !sendEmail}
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete Incident Report
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to delete this incident report? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deleteTarget && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
              <p className="text-red-400 font-medium">{deleteTarget.title}</p>
              <p className="text-zinc-400 text-sm mt-1">IR#: {deleteTarget.ir_number || deleteTarget.id}</p>
              <p className="text-zinc-500 text-xs mt-1">Site: {deleteTarget.site_name}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteTarget(null);
              }} 
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteIncident}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              {bulkArchiveMode ? <Archive className="w-5 h-5" /> : <Trash2 className="w-5 h-5" />}
              {bulkArchiveMode ? 'Archive' : 'Delete'} {selectedIds.length} Incidents
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {bulkArchiveMode 
                ? 'Archived incidents can be recovered later. This helps clean up your feed.'
                : 'This action permanently deletes the selected incidents. This cannot be undone.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-[#09090b] rounded border border-[#3f3f46]">
              <p className="text-zinc-300">
                You are about to {bulkArchiveMode ? 'archive' : 'delete'}{' '}
                <span className="font-bold text-white">{selectedIds.length}</span> incident reports.
              </p>
            </div>
            
            {!bulkArchiveMode && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded">
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Warning: Deleted records cannot be recovered!
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowBulkDeleteDialog(false)}
              className="border-zinc-600"
              disabled={bulkDeleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDelete}
              className={bulkArchiveMode ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700"}
              disabled={bulkDeleting}
              data-testid="confirm-bulk-delete-btn"
            >
              {bulkDeleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : bulkArchiveMode ? (
                <Archive className="w-4 h-4 mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {bulkArchiveMode ? 'Archive' : 'Delete'} {selectedIds.length} Incidents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
